import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';
import ReminderService from '../services/ReminderService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { readFilesAsStoredAttachments } from '../utils/fileAttachments';

const statusLabels = {
  available: 'Available',
  checked_out: 'Checked Out',
  under_repair: 'Under Repair',
  retired: 'Retired'
};

const incidentTypes = ['Injury', 'Safeguarding concern', 'Property damage', 'Theft', 'Behavioural', 'Other'];

export default function InventoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const {
    inventoryItems,
    inventoryController,
    incidentController,
    students
  } = useStudentStore();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const reminderService = useMemo(() => new ReminderService(), []);

  const item = inventoryItems.find((entry) => entry.id === id) || inventoryController.getItem(id);
  const activeAssignment = item ? inventoryController.getActiveAssignment(item) : null;
  const dueStatus = item ? inventoryController.getDueStatus(item) : { isOverdue: false };

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [reminderModal, setReminderModal] = useState(null);
  const [reminderMessage, setReminderMessage] = useState('');
  const [showCreatedToast, setShowCreatedToast] = useState(Boolean(location.state?.created));
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    type: '',
    serialNumber: '',
    condition: 'Good',
    status: 'available',
    notes: ''
  });

  const [assignmentForm, setAssignmentForm] = useState({
    assignedToType: 'student',
    assignedToId: '',
    checkoutDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    expectedReturnDate: '',
    notes: ''
  });

  const assignmentOptions = useMemo(() => {
    const list = assignmentForm.assignedToType === 'student' ? students : staffList;
    return list.map((person) => ({
      value: person.id,
      label: person.name,
      meta: assignmentForm.assignedToType === 'student'
        ? person.reference || person.id
        : person.role || person.title || ''
    }));
  }, [assignmentForm.assignedToType, staffList, students]);
  const staffNameOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );

  const [incidentForm, setIncidentForm] = useState({
    type: 'Property damage',
    date: new Date().toISOString().slice(0, 16),
    severity: 'medium',
    status: 'open',
    description: '',
    assignedKeyWorker: '',
    followUpRequired: true,
    followUpDate: '',
    followUpPrompt: ''
  });
  const [incidentParticipants, setIncidentParticipants] = useState({
    students: [],
    staff: []
  });
  const [incidentFiles, setIncidentFiles] = useState([]);

  useEffect(() => {
    if (!location.state?.created) return;
    setShowCreatedToast(true);
    const timer = setTimeout(() => setShowCreatedToast(false), 4500);
    return () => clearTimeout(timer);
  }, [location.state]);

  const openEditModal = () => {
    if (!item) return;
    setEditForm({
      name: item.name,
      type: item.type,
      serialNumber: item.serialNumber || '',
      condition: item.condition || 'Good',
      status: item.status || 'available',
      notes: item.notes || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!item || !editForm.name.trim()) return;
    inventoryController.updateItem(
      item.id,
      {
        name: editForm.name.trim(),
        type: editForm.type,
        serialNumber: editForm.serialNumber.trim(),
        condition: editForm.condition,
        status: editForm.status,
        notes: editForm.notes
      },
      user
    );
    setEditModalOpen(false);
  };

  const incidentsForItem = useMemo(() => {
    if (!item) return [];
    return incidentController.listByInventoryItem(item.id);
  }, [item, incidentController]);

  const openAssignModal = () => {
    setAssignmentForm((prev) => ({
      ...prev,
      assignedToId: ''
    }));
    setAssignModalOpen(true);
  };

  const handleAssign = () => {
    if (!assignmentForm.assignedToId) return;
    const assignedToName =
      assignmentForm.assignedToType === 'student'
        ? students.find((student) => student.id === assignmentForm.assignedToId)?.name
        : staffList.find((staff) => staff.id === assignmentForm.assignedToId)?.name;

    inventoryController.assignItem(
      item.id,
      {
        ...assignmentForm,
        assignedToName: assignedToName || ''
      },
      user
    );
    setAssignModalOpen(false);
  };

  const handleCheckIn = (assignmentId) => {
    inventoryController.checkInItem(item.id, assignmentId, user);
  };

  const openReminder = () => {
    if (!activeAssignment) return;
    const staff = staffList.find(
      (member) =>
        member.id === activeAssignment.assignedToId || member.name === activeAssignment.assignedToName
    );
    const entityId = `${item.id}:${activeAssignment.id}`;
    const lastReminder = staff ? reminderService.getLastReminder(entityId, staff.id) : null;
    setReminderMessage('');
    setReminderModal({ staff, lastReminder });
  };

  const submitIncident = async () => {
    if (!incidentForm.description.trim()) return;
    const participants = [
      ...incidentParticipants.students.map((studentId) => {
        const student = students.find((s) => s.id === studentId);
        return { id: studentId, type: 'student', name: student?.name || '', role: 'Student' };
      }),
      ...incidentParticipants.staff.map((staffId) => {
        const staff = staffList.find((s) => s.id === staffId);
        return { id: staffId, type: 'staff', name: staff?.name || '', role: staff?.role || 'Support Worker' };
      })
    ];
    const documents = await readFilesAsStoredAttachments(incidentFiles, user?.name || 'System');

    incidentController.createIncident(
      {
        type: incidentForm.type,
        date: incidentForm.date,
        severity: incidentForm.severity,
        status: incidentForm.status,
        reportedBy: user?.name || 'System',
        description: incidentForm.description.trim(),
        assignedKeyWorker: incidentForm.assignedKeyWorker,
        followUpRequired: incidentForm.followUpRequired,
        followUpDate: incidentForm.followUpRequired ? incidentForm.followUpDate : null,
        followUpPrompt: incidentForm.followUpRequired ? incidentForm.followUpPrompt : '',
        participants,
        inventoryItemIds: [item.id],
        documents
      },
      user
    );

    setIncidentFiles([]);
    setIncidentForm((prev) => ({
      ...prev,
      description: '',
      followUpDate: '',
      followUpPrompt: ''
    }));
    setIncidentParticipants({ students: [], staff: [] });
    setIncidentModalOpen(false);
  };

  const toggleParticipant = (type, id) => {
    setIncidentParticipants((prev) => {
      const next = new Set(prev[type]);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prev, [type]: [...next] };
    });
  };

  if (!item) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Item not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/inventory')}>
          Back to inventory
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {showCreatedToast && (
        <div className="sp-alert sp-alert--success flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="sp-value" style={{ fontWeight: 600 }}>Inventory item created</p>
            <p className="text-sm sp-muted">Next step: assign, edit details, or add another item.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={openAssignModal}>Check out</Button>
            <Button size="sm" variant="secondary" onClick={openEditModal}>Edit</Button>
            <Button size="sm" onClick={() => navigate('/inventory/new')}>Add another</Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{item.name}</h2>
          <p className="sp-muted">{item.reference} · {item.type} · {item.serialNumber || 'No serial'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/inventory')}>Back to inventory</Button>
          <Button variant="secondary" onClick={openEditModal}>Edit item</Button>
          <Button onClick={openAssignModal}>{activeAssignment ? 'Reassign' : 'Assign item'}</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-4">
        <div className="sp-panel p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={item.status === 'checked_out' ? 'review' : item.status === 'under_repair' ? 'inactive' : 'active'}>
              {statusLabels[item.status] || item.status}
            </Badge>
            {dueStatus.isOverdue && <Badge variant="danger">Overdue</Badge>}
            {!dueStatus.isOverdue && dueStatus.isDueSoon && <Badge variant="review">Due soon</Badge>}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="sp-label">Condition</div>
              <div className="text-sm">{item.condition}</div>
            </div>
            <div>
              <div className="sp-label">Notes</div>
              <div className="text-sm">{item.notes || '—'}</div>
            </div>
          </div>

          <div>
            <div className="sp-label">Assignments</div>
            <div className="mt-2 space-y-2">
              {item.assignments.length === 0 && <p className="text-sm sp-muted">No assignments recorded.</p>}
              {item.assignments.map((assignment) => (
                <div key={assignment.id} className="sp-panel sp-panel--muted p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm" style={{ fontWeight: 600 }}>{assignment.assignedToName || assignment.assignedToId}</div>
                    <div className="text-xs sp-muted">
                      {assignment.assignedToType} · Checked out {formatDate(assignment.checkoutDate)}
                      {assignment.dueDate ? ` · Due ${formatDate(assignment.dueDate)}` : ''}
                      {assignment.checkinDate ? ` · Returned ${formatDate(assignment.checkinDate)}` : ''}
                    </div>
                  </div>
                  {!assignment.checkinDate && (
                    <Button size="sm" variant="secondary" onClick={() => handleCheckIn(assignment.id)}>
                      Check in
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {dueStatus.isOverdue && (
            <div className="sp-panel sp-panel--muted p-3 flex items-center justify-between">
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>Overdue item</div>
                <p className="text-sm sp-muted">Send an automated reminder to the assignee.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={openReminder}>Send reminder</Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="sp-panel p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="sp-label">Linked incidents</div>
                <div className="text-xs sp-muted">{incidentsForItem.length} recorded</div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setIncidentModalOpen(true)}>
                Log incident
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {incidentsForItem.length === 0 && <p className="text-sm sp-muted">No linked incidents.</p>}
              {incidentsForItem.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/incidents/${incident.id}`)}
                >
                  {incident.type} · {formatDate(incident.date)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assign inventory item</h3>
              <p className="text-sm sp-muted">Assign to a student or staff member.</p>
            </div>
            <Button variant="ghost" onClick={() => setAssignModalOpen(false)}>Close</Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Assign to</label>
              <select
                className="sp-select w-full"
                value={assignmentForm.assignedToType}
                onChange={(e) => setAssignmentForm((prev) => ({ ...prev, assignedToType: e.target.value, assignedToId: '' }))}
              >
                <option value="student">Student</option>
                <option value="staff">Support worker</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Name</label>
              <SearchableSelect
                value={assignmentForm.assignedToId}
                options={assignmentOptions}
                placeholder={`Search ${assignmentForm.assignedToType === 'student' ? 'students' : 'staff'}`}
                emptyLabel={`No ${assignmentForm.assignedToType === 'student' ? 'students' : 'staff'} found.`}
                ariaLabel="Search assignee"
                onChange={(value) => setAssignmentForm((prev) => ({ ...prev, assignedToId: value }))}
              />
            </div>
            <div>
              <label className="sp-label">Check-out date</label>
              <div className="mt-2">
                <DateSelect
                  value={assignmentForm.checkoutDate}
                  onChange={(value) => setAssignmentForm((prev) => ({ ...prev, checkoutDate: value }))}
                />
              </div>
            </div>
            <div>
              <label className="sp-label">Due date</label>
              <div className="mt-2">
                <DateSelect
                  value={assignmentForm.dueDate}
                  onChange={(value) => setAssignmentForm((prev) => ({ ...prev, dueDate: value }))}
                />
              </div>
            </div>
            <div>
              <label className="sp-label">Expected return</label>
              <div className="mt-2">
                <DateSelect
                  value={assignmentForm.expectedReturnDate}
                  onChange={(value) => setAssignmentForm((prev) => ({ ...prev, expectedReturnDate: value }))}
                />
              </div>
            </div>
            <div>
              <label className="sp-label">Notes</label>
              <input
                className="sp-input w-full"
                value={assignmentForm.notes}
                onChange={(e) => setAssignmentForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign}>Assign</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={incidentModalOpen} onClose={() => setIncidentModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Log incident</h3>
              <p className="text-sm sp-muted">Record damage, loss, or safeguarding incidents.</p>
            </div>
            <Button variant="ghost" onClick={() => setIncidentModalOpen(false)}>Close</Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Type</label>
              <select
                className="sp-select w-full"
                value={incidentForm.type}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                {incidentTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Date & time</label>
              <input
                type="datetime-local"
                className="sp-input w-full"
                value={incidentForm.date}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                className="sp-select w-full"
                value={incidentForm.severity}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, severity: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                className="sp-select w-full"
                value={incidentForm.status}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="open">Open</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="sp-label">Description</label>
            <textarea
              className="sp-textarea w-full"
              rows={4}
              value={incidentForm.description}
              onChange={(e) => setIncidentForm((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Assigned key worker</label>
              <SearchableSelect
                value={incidentForm.assignedKeyWorker}
                options={[{ value: '', label: 'Unassigned' }, ...staffNameOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search key worker"
                className="mt-2"
                onChange={(value) => setIncidentForm((prev) => ({ ...prev, assignedKeyWorker: value }))}
              />
            </div>
            <div>
              <label className="sp-label">Follow-up date</label>
              <div className="mt-2">
                <DateSelect
                  value={incidentForm.followUpDate}
                  onChange={(value) =>
                    setIncidentForm((prev) => ({
                      ...prev,
                      followUpDate: value,
                      followUpRequired: value ? true : prev.followUpRequired
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div>
            <label className="sp-label">Follow-up prompt</label>
            <input
              className="sp-input w-full"
              value={incidentForm.followUpPrompt}
              onChange={(e) => setIncidentForm((prev) => ({ ...prev, followUpPrompt: e.target.value }))}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Students involved</label>
              <SearchableChecklist
                className="mt-2"
                items={students}
                selectedIds={incidentParticipants.students}
                onToggle={(id) => toggleParticipant('students', id)}
                placeholder="Search students"
                getLabel={(student) => student.name}
                getMeta={(student) => student.reference || student.id}
                emptyLabel="No students found."
              />
            </div>
            <div>
              <label className="sp-label">Support workers involved</label>
              <SearchableChecklist
                className="mt-2"
                items={staffList}
                selectedIds={incidentParticipants.staff}
                onToggle={(id) => toggleParticipant('staff', id)}
                placeholder="Search staff"
                getLabel={(staff) => staff.name}
                getMeta={(staff) => staff.role || staff.title || ''}
                emptyLabel="No staff found."
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Attachments</label>
            <input type="file" multiple onChange={(e) => setIncidentFiles(Array.from(e.target.files || []))} />
            {incidentFiles.length > 0 && (
              <div className="text-xs sp-muted mt-2">
                {incidentFiles.map((file) => file.name).join(', ')}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIncidentModalOpen(false)}>Cancel</Button>
            <Button onClick={submitIncident}>Save incident</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(reminderModal)} onClose={() => setReminderModal(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Send overdue reminder</h3>
              <p className="text-sm sp-muted">Uses the reminder template configured in Settings.</p>
            </div>
            <Button variant="ghost" onClick={() => setReminderModal(null)}>Close</Button>
          </div>
          {reminderModal && (
            <div className="sp-panel sp-panel--muted p-3 text-sm">
              <div className="sp-value" style={{ fontWeight: 600 }}>{item.name}</div>
              <div className="text-xs sp-muted mt-1">
                Assigned to {reminderModal.staff?.name || activeAssignment?.assignedToName || 'Unassigned'}
                {activeAssignment?.dueDate ? ` · Due ${formatDate(activeAssignment.dueDate)}` : ''}
              </div>
              {reminderModal.lastReminder && (
                <div className="text-xs sp-muted mt-1">
                  Last reminder: {formatDateTime(reminderModal.lastReminder.sentAt)}
                </div>
              )}
              {!reminderModal.staff?.loginEmail && (
                <div className="text-xs sp-warning-text mt-1">No staff email configured.</div>
              )}
            </div>
          )}
          {reminderMessage && <div className="text-xs sp-warning-text">{reminderMessage}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReminderModal(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!reminderModal?.staff) {
                  setReminderMessage('No staff email configured for this assignment.');
                  return;
                }
                const result = reminderService.sendInventoryReminder({
                  item,
                  assignment: activeAssignment,
                  staffId: reminderModal.staff.id,
                  forced: false
                });
                if (result.status === 'throttled') {
                  setReminderMessage(result.error);
                  return;
                }
                setReminderModal(null);
              }}
              disabled={
                reminderModal?.staff
                  ? reminderService.isThrottled(
                      `${item.id}:${activeAssignment?.id}`,
                      reminderModal.staff.id
                    )
                  : true
              }
            >
              Send reminder
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                if (!reminderModal?.staff) {
                  setReminderMessage('No staff email configured for this assignment.');
                  return;
                }
                reminderService.sendInventoryReminder({
                  item,
                  assignment: activeAssignment,
                  staffId: reminderModal.staff.id,
                  forced: true
                });
                setReminderModal(null);
              }}
              disabled={!reminderModal?.staff}
            >
              Resend now
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Edit item</h3>
              <p className="text-sm sp-muted">Update inventory details.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setEditModalOpen(false)}>Close</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Item name</label>
              <input
                className="sp-input w-full"
                value={editForm.name}
                onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <input
                className="sp-input w-full"
                value={editForm.type}
                onChange={(event) => setEditForm((prev) => ({ ...prev, type: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Serial number</label>
              <input
                className="sp-input w-full"
                value={editForm.serialNumber}
                onChange={(event) => setEditForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Condition</label>
              <select
                className="sp-select w-full"
                value={editForm.condition}
                onChange={(event) => setEditForm((prev) => ({ ...prev, condition: event.target.value }))}
              >
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
                <option value="Damaged">Damaged</option>
                <option value="Lost">Lost</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                className="sp-select w-full"
                value={editForm.status}
                onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
              >
                <option value="available">Available</option>
                <option value="checked_out">Checked Out</option>
                <option value="under_repair">Under Repair</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Notes</label>
              <textarea
                className="sp-textarea w-full"
                rows={3}
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.name.trim()}>Save changes</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
